import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import {
  BarChart3,
  Bell,
  ChevronsLeft,
  ChevronsRight,
  FolderKanban,
  GraduationCap,
  Inbox,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Search,
  Settings,
  Shield,
  Sun,
  Users,
  Languages,
  Keyboard,
} from 'lucide-react';
import {
  Avatar,
  Button,
  cn,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Kbd,
  Tooltip,
} from '@vwt/ui';
import { useSession, useMe } from '../lib/session.js';
import { useTranslation, applyLocale, currentLocale } from '../lib/i18n.js';
import { useTheme } from '../lib/theme.js';
import { useHotkeys } from '../lib/hotkeys.js';
import { api, useApiMutation } from '../lib/api.js';
import { CommandPalette } from './command-palette.js';
import { toastError } from '../lib/errors.js';

const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'es', label: 'Español' },
] as const;

function NavItem({ to, icon: Icon, label, collapsed, onNavigate }: { to: string; icon: typeof Inbox; label: string; collapsed: boolean; onNavigate?: () => void }) {
  const link = (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex h-8 items-center gap-2.5 rounded-[var(--radius)] px-2.5 text-sm font-medium transition-colors',
          isActive ? 'bg-accent-soft text-accent-soft-fg' : 'text-fg-muted hover:bg-muted hover:text-fg',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className={cn(collapsed && 'sr-only')}>{label}</span>
    </NavLink>
  );
  return collapsed ? (
    <Tooltip content={label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const items = [
    { to: '/my-work', icon: LayoutDashboard, label: t('nav.myWork') },
    { to: '/requests', icon: Inbox, label: t('nav.requests') },
    { to: '/work', icon: ListTodo, label: t('nav.work') },
    { to: '/projects', icon: FolderKanban, label: t('nav.projects') },
    { to: '/teams', icon: Users, label: t('nav.teams') },
    { to: '/reports', icon: BarChart3, label: t('nav.reports') },
  ];
  return (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 px-2">
      {items.map((item) => (
        <NavItem key={item.to} {...item} collapsed={collapsed} onNavigate={onNavigate} />
      ))}
      <div className="mt-auto flex flex-col gap-0.5 pb-2">
        <NavItem to="/tutorial" icon={GraduationCap} label={t('nav.tutorial')} collapsed={collapsed} onNavigate={onNavigate} />
        {me.permissions.isAdmin ? <NavItem to="/admin" icon={Shield} label={t('nav.admin')} collapsed={collapsed} onNavigate={onNavigate} /> : null}
        <NavItem to="/settings" icon={Settings} label={t('nav.settings')} collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </nav>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const me = useMe();
  const { signOut, refreshMe } = useSession();
  const [theme, setTheme] = useTheme();
  const updateMe = useApiMutation('updateMe');
  const setLocale = async (locale: string) => {
    applyLocale(locale);
    try {
      await updateMe.mutateAsync({ body: { locale: locale as 'en' } });
      await refreshMe();
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-[var(--radius)] p-1 hover:bg-muted" aria-label={me.displayName}>
          <Avatar name={me.displayName} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-fg">
          <div className="text-sm font-medium">{me.displayName}</div>
          <div className="truncate text-xs font-normal text-fg-subtle">{me.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages /> {t('nav.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={currentLocale()} onValueChange={(v) => void setLocale(v)}>
              {LOCALES.map((l) => (
                <DropdownMenuRadioItem key={l.value} value={l.value}>
                  {l.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            {theme === 'dark' ? <Moon /> : theme === 'light' ? <Sun /> : <Monitor />} {t('nav.theme')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as 'light')}>
              <DropdownMenuRadioItem value="light">{t('nav.themeLight')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">{t('nav.themeDark')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">{t('nav.themeSystem')}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={() => window.dispatchEvent(new CustomEvent('vit:shortcuts'))}>
          <Keyboard /> {t('nav.shortcuts')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut /> {t('nav.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ShortcutsDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('vit:shortcuts', show);
    return () => window.removeEventListener('vit:shortcuts', show);
  }, []);
  const rows: Array<[ReactNode, string]> = [
    [<Kbd key="c">C</Kbd>, t('nav.newWork')],
    [<Kbd key="r">R</Kbd>, t('nav.newRequest')],
    [<Kbd key="/">/</Kbd>, t('nav.search')],
    [
      <span key="k" className="inline-flex gap-1">
        <Kbd>Ctrl</Kbd>
        <Kbd>K</Kbd>
      </span>,
      t('nav.search'),
    ],
    [<Kbd key="esc">Esc</Kbd>, t('common.close')],
    [
      <span key="enter" className="inline-flex gap-1">
        <Kbd>Ctrl</Kbd>
        <Kbd>Enter</Kbd>
      </span>,
      t('common.submit'),
    ],
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent title={t('nav.shortcuts')}>
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
          {rows.map(([keys, label], i) => (
            <div key={i} className="contents">
              <dt>{keys}</dt>
              <dd className="text-fg-muted">{label}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const { runtime } = useSession();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('vwt.sidebar') === 'collapsed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem('vwt.sidebar', c ? 'expanded' : 'collapsed');
      return !c;
    });
  };

  const bindings = useMemo(
    () => ({
      c: () => navigate('/work?create=1'),
      r: () => navigate('/requests/new'),
      '/': () => setPaletteOpen(true),
      'mod+k': () => setPaletteOpen(true),
    }),
    [navigate],
  );
  useHotkeys(bindings);

  return (
    <div className="flex h-full flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-surface focus:p-2">
        {t('app.skipToContent')}
      </a>
      {runtime?.fixtureMode ? (
        <div role="status" className="flex items-center justify-center gap-2 bg-warning-soft px-3 py-1 text-center text-xs font-medium text-warning">
          {t('app.fixtureBanner')}
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <aside className={cn('hidden shrink-0 flex-col border-r border-border bg-surface transition-[width] md:flex', collapsed ? 'w-14' : 'w-60')}>
          <div className={cn('flex h-12 items-center gap-2 px-3', collapsed && 'justify-center px-0')}>
            <img src="/icon.png" alt="" className="size-7" />
            {!collapsed ? <span className="truncate text-sm font-semibold">{t('app.name')}</span> : null}
          </div>
          <div className={cn('px-2 pb-2', collapsed && 'flex justify-center')}>
            {collapsed ? (
              <Tooltip content={t('nav.newRequest')} side="right">
                <Button size="icon" variant="primary" aria-label={t('nav.newRequest')} onClick={() => navigate('/requests/new')}>
                  <Plus />
                </Button>
              </Tooltip>
            ) : (
              <Button variant="primary" className="w-full justify-start" onClick={() => navigate('/requests/new')}>
                <Plus /> {t('nav.newRequest')}
              </Button>
            )}
          </div>
          <SidebarContent collapsed={collapsed} />
          <button onClick={toggle} className="flex h-10 items-center justify-center border-t border-border text-fg-subtle hover:text-fg" aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}>
            {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
          </button>
        </aside>

        {/* Mobile navigation drawer */}
        <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
          <DialogContent title={t('app.name')} className="top-0 left-0 h-full max-h-full w-72 translate-x-0 translate-y-0 rounded-none">
            <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </DialogContent>
        </Dialog>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
            <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('nav.openMenu')} onClick={() => setMobileOpen(true)}>
              <Menu />
            </Button>
            <button
              onClick={() => setPaletteOpen(true)}
              className="flex h-8 w-full max-w-md items-center gap-2 rounded-[var(--radius)] border border-border bg-surface-2 px-2.5 text-sm text-fg-subtle hover:border-border-strong"
            >
              <Search className="size-4" />
              <span className="truncate">{t('nav.searchPlaceholder')}</span>
              <Kbd className="ml-auto hidden sm:inline-flex">/</Kbd>
            </button>
            <div className="ml-auto flex items-center gap-1">
              <Button variant="secondary" size="sm" className="hidden sm:inline-flex" onClick={() => navigate('/work?create=1')}>
                <Plus /> {t('nav.newWork')}
              </Button>
              <NotificationsButton />
              <UserMenu />
            </div>
          </header>
          <main id="main" ref={mainRef} className="min-h-0 flex-1 overflow-y-auto" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsDialog />
    </div>
  );
}

function NotificationsButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Tooltip content={t('nav.notifications')}>
      <Button variant="ghost" size="icon" aria-label={t('nav.notifications')} onClick={() => navigate('/settings?tab=notifications')}>
        <Bell />
      </Button>
    </Tooltip>
  );
}

export { api };
